// Renders a shareable 1080x1350 result card onto a canvas and returns a PNG Blob.
const W = 1080;
const H = 1350;
const PAD = 88;
const C = {
  bg: "#06241B",
  bg2: "#0A2E23",
  bone: "#FDFBF7",
  gold: "#D4AF37",
  terracotta: "#E07A66",
  muted: "rgba(253,251,247,0.62)",
  track: "rgba(253,251,247,0.16)",
};
const DISPLAY = '"Cormorant Garamond", Georgia, serif';
const BODY = '"Manrope", Arial, sans-serif';

const wrap = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = test;
  });
  if (line) lines.push(line);
  return lines;
};

const drawLines = (ctx, lines, x, y, lineHeight) => {
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return y + lines.length * lineHeight;
};

const grain = (ctx) => {
  const tmp = document.createElement("canvas");
  tmp.width = W;
  tmp.height = H;
  const tctx = tmp.getContext("2d");
  const img = tctx.createImageData(W, H);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = Math.random() * 255;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
    img.data[i + 3] = 14;
  }
  tctx.putImageData(img, 0, 0);
  ctx.drawImage(tmp, 0, 0);
};

export const renderResultCard = async (result, site) => {
  await document.fonts.ready;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // background + grain + thin frame
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);
  grain(ctx);
  ctx.strokeStyle = "rgba(212,175,55,0.45)";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  // eyebrow
  ctx.fillStyle = C.terracotta;
  ctx.font = `700 22px ${BODY}`;
  ctx.letterSpacing = "6px";
  ctx.fillText("MONEY PASSBOOK  ·  FINAL ENTRY", PAD, 150);
  ctx.letterSpacing = "0px";

  // personality
  ctx.fillStyle = C.muted;
  ctx.font = `400 26px ${BODY}`;
  ctx.fillText("My Money Personality", PAD, 230);
  ctx.fillStyle = C.bone;
  ctx.font = `500 96px ${DISPLAY}`;
  const nameLines = wrap(ctx, result.personality, W - PAD * 2 - 260);
  let y = drawLines(ctx, nameLines, PAD, 320, 96);

  // score seal (top right)
  const sx = W - PAD - 110;
  const sy = 300;
  ctx.beginPath();
  ctx.arc(sx, sy, 108, 0, Math.PI * 2);
  ctx.fillStyle = C.gold;
  ctx.fill();
  ctx.fillStyle = C.bg;
  ctx.textAlign = "center";
  ctx.font = `600 104px ${DISPLAY}`;
  ctx.fillText(String(result.score), sx - 14, sy + 30);
  ctx.font = `500 34px ${DISPLAY}`;
  ctx.fillText("/10", sx + 56, sy + 28);
  ctx.font = `700 15px ${BODY}`;
  ctx.letterSpacing = "3px";
  ctx.fillText("WELL-BEING", sx, sy + 68);
  ctx.letterSpacing = "0px";
  ctx.textAlign = "left";

  // divider
  y = Math.max(y + 10, 470);
  ctx.fillStyle = C.track;
  ctx.fillRect(PAD, y, W - PAD * 2, 2);
  y += 50;

  // area bars
  const barW = W - PAD * 2;
  result.areas.forEach((a) => {
    ctx.fillStyle = C.bone;
    ctx.font = `600 27px ${BODY}`;
    ctx.fillText(a.label, PAD, y);
    ctx.textAlign = "right";
    ctx.fillStyle = C.gold;
    ctx.font = `500 34px ${DISPLAY}`;
    ctx.fillText(`${a.pct}%`, PAD + barW, y + 2);
    ctx.textAlign = "left";
    ctx.fillStyle = C.track;
    ctx.fillRect(PAD, y + 20, barW, 6);
    ctx.fillStyle = C.gold;
    ctx.fillRect(PAD, y + 20, (barW * a.pct) / 100, 6);
    y += 80;
  });

  // strongest / work on
  y += 10;
  const col2 = W / 2 + 20;
  ctx.fillStyle = C.muted;
  ctx.font = `700 18px ${BODY}`;
  ctx.letterSpacing = "4px";
  ctx.fillText("STRONGEST AREA", PAD, y);
  ctx.fillText("AREA TO WORK ON", col2, y);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = C.bone;
  ctx.font = `500 44px ${DISPLAY}`;
  ctx.fillText(result.strongest_area, PAD, y + 56);
  ctx.fillText(result.weakest_area, col2, y + 56);
  y += 120;

  // next action panel
  ctx.fillStyle = C.bg2;
  ctx.fillRect(PAD, y, W - PAD * 2, 180);
  ctx.fillStyle = C.gold;
  ctx.fillRect(PAD, y, 6, 180);
  ctx.fillStyle = C.terracotta;
  ctx.font = `700 18px ${BODY}`;
  ctx.letterSpacing = "4px";
  ctx.fillText("MY NEXT ACTION", PAD + 40, y + 52);
  ctx.letterSpacing = "0px";
  ctx.fillStyle = C.bone;
  ctx.font = `400 34px ${BODY}`;
  drawLines(ctx, wrap(ctx, result.next_action, W - PAD * 2 - 80).slice(0, 2), PAD + 40, y + 104, 44);

  // footer
  ctx.fillStyle = C.muted;
  ctx.font = `400 24px ${BODY}`;
  ctx.fillText(`Take the passbook at ${site.domain}/quiz`, PAD, H - 106);
  ctx.font = `500 30px ${DISPLAY}`;
  ctx.fillStyle = C.gold;
  ctx.fillText(`${site.company}  ·  ${site.coach}`, PAD, H - 66);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};
